/**
 * Document Style — Settings → Company Branding (below the colour palette).
 * Font, layout template, logo size/position + tagline, footer/terms text,
 * show/hide tax ID & PO, custom .ttf upload and a live sample-invoice PDF preview.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { Type, Loader2, Save, FileText, Upload } from 'lucide-react';
import { FONT_CSS } from '../utils/brandFont';
import { ColumnLayoutEditor } from './ColumnLayoutEditor';
import { NumberingFields, TemplateDefaultsFields } from './InvoiceNumberingFields';
import { LabelsEditor, DocumentAuditList } from './DocumentLayoutBits';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  font_family: 'helvetica', layout: 'classic', logo_size: 'm', logo_position: 'left',
  tagline: '', payment_instructions: '', thank_you_note: '', show_tax_id: true, show_po: true,
};
const LAYOUTS = [
  { key: 'classic', label: 'Classic', hint: 'Logo left, title right, hairline divider (current default).' },
  { key: 'modern', label: 'Modern', hint: 'Colour header band, white title, oversized total.' },
  { key: 'minimal', label: 'Minimal', hint: 'Mono text, thin rules, no colour fills.' },
];

export default function DocumentStyleCard({ token, company }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ ...DEFAULTS, ...(company?.document_branding || {}) });
  const [fonts, setFonts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [colSchema, setColSchema] = useState(null);
  const [colDoc, setColDoc] = useState('daily');
  const [columns, setColumns] = useState({});
  const [labels, setLabels] = useState({});
  const [savingCols, setSavingCols] = useState(false);
  const [colNote, setColNote] = useState('');
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [tplDefaults, setTplDefaults] = useState({});
  const [tplNumbering, setTplNumbering] = useState({});
  const [tplUseNumbering, setTplUseNumbering] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [audit, setAudit] = useState([]);
  const loadPresetsAudit = () => {
    axios.get(`${API}/company/document-presets`, auth).then((r) => setPresets(r.data.presets || [])).catch(() => {});
    axios.get(`${API}/company/document-audit?limit=15`, auth).then((r) => setAudit(r.data.items || [])).catch(() => {});
  };
  const [uploading, setUploading] = useState('');
  const fileRef = useRef(null);
  const boldRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/company/document-branding/fonts`, auth).then((r) => setFonts(r.data.fonts || [])).catch(() => {});
    axios.get(`${API}/document-columns/schema`, auth).then((r) => {
      setColSchema(r.data);
      const stored = company?.document_branding?.columns || {};
      setColumns({ daily: stored.daily || null, invoice: stored.invoice || null, estimate: stored.estimate || null });
      const lb = company?.document_branding?.labels || {};
      setLabels({ daily: lb.daily || {}, invoice: lb.invoice || {}, estimate: lb.estimate || {} });
    }).catch(() => {});
    loadPresetsAudit();
  }, [token]); // eslint-disable-line

  const savePreset = async () => {
    if (!presetName.trim()) return;
    try {
      const shown = Object.fromEntries(['daily', 'invoice', 'estimate'].map((dt) => [dt, columns[dt] || colSchema?.company?.[dt] || colSchema?.defaults?.[dt] || null]));
      const extras = { defaults: tplDefaults, numbering: tplUseNumbering ? tplNumbering : null };
      const res = editingPresetId
        ? await axios.put(`${API}/company/document-presets/${editingPresetId}`, { name: presetName.trim(), columns: shown, labels, ...extras }, auth)
        : await axios.post(`${API}/company/document-presets`, { name: presetName.trim(), columns: shown, labels, ...extras }, auth);
      setPresets(res.data.presets || []);
      setEditingPresetId(null); setTplDefaults({}); setTplNumbering({}); setTplUseNumbering(false); setPresetName('');
      toast({ title: 'Preset saved', description: `“${res.data.preset.name}” can now be applied to any customer.` });
      loadPresetsAudit();
    } catch (e) {
      toast({ title: 'Preset not saved', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };
  const deletePreset = async (id) => {
    try {
      const res = await axios.delete(`${API}/company/document-presets/${id}`, auth);
      setPresets(res.data.presets || []);
      loadPresetsAudit();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };
  const loadPresetHere = (p) => {
    setColumns({ daily: p.columns?.daily || null, invoice: p.columns?.invoice || null, estimate: p.columns?.estimate || null });
    setLabels({ daily: p.labels?.daily || {}, invoice: p.labels?.invoice || {}, estimate: p.labels?.estimate || {} });
    toast({ title: `Loaded “${p.name}”`, description: 'Review and click Save column layout to make it the company default.' });
  };

  const saveColumns = async () => {
    setSavingCols(true);
    try {
      const res = await axios.put(`${API}/company/document-branding`, { columns, labels, change_note: colNote }, auth);
      const stored = res.data.document_branding?.columns || {};
      setColumns({ daily: stored.daily || null, invoice: stored.invoice || null, estimate: stored.estimate || null });
      const lb = res.data.document_branding?.labels || {};
      setLabels({ daily: lb.daily || {}, invoice: lb.invoice || {}, estimate: lb.estimate || {} });
      setColNote('');
      toast({ title: 'Column layout saved', description: 'New documents use these columns and labels unless a customer has its own layout.' });
      loadPresetsAudit();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSavingCols(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.keys(DEFAULTS).map((k) => [k, form[k]]));
      const res = await axios.put(`${API}/company/document-branding`, payload, auth);
      setForm({ ...DEFAULTS, ...(res.data.document_branding || {}) });
      toast({ title: 'Document style saved', description: 'Invoices, estimates, daily site invoices and the customer portal now use this style.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const upload = async (file, variant) => {
    if (!file) return;
    setUploading(variant);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/company/document-branding/font?variant=${variant}`, fd, auth);
      setForm({ ...DEFAULTS, ...(res.data.document_branding || {}) });
      toast({ title: `${variant === 'bold' ? 'Bold' : 'Regular'} font uploaded`, description: `${res.data.font_name || 'Custom font'} is now selected.` });
    } catch (e) {
      toast({ title: 'Font upload failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setUploading(''); }
  };

  const openPreview = async () => {
    try {
      const res = await axios.get(`${API}/company/document-branding/preview.pdf`, { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank', 'noopener');
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const fontCss = form.font_family === 'custom' ? `'${form.custom_font_name || 'Custom'}', sans-serif` : (FONT_CSS[form.font_family] || FONT_CSS.helvetica);

  return (
    <Card data-testid="document-style-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Type className="w-5 h-5" /> Document style</CardTitle>
        <CardDescription>
          Fonts, layout, logo placement and footer text for every customer-facing document — invoices, estimates,
          daily site invoices, the customer portal and the public estimate page. Save, then open the sample PDF to check it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Font</Label>
            <Select value={form.font_family} onValueChange={(v) => set('font_family', v)}>
              <SelectTrigger data-testid="docstyle-font-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fonts.map((f) => <SelectItem key={f.key} value={f.key} data-testid={`docstyle-font-${f.key}`}><span style={{ fontFamily: f.css }}>{f.label}</span></SelectItem>)}
                {form.custom_font_s3_key && <SelectItem value="custom" data-testid="docstyle-font-custom">Custom — {form.custom_font_name || 'uploaded font'}</SelectItem>}
              </SelectContent>
            </Select>
            <p className="text-[13px] mt-2 px-3 py-2 rounded-md bg-slate-800/60 text-slate-100" style={{ fontFamily: fontCss }} data-testid="docstyle-font-sample">
              Invoice INV-2026-1001 · Total $697.78 — The quick brown fox jumps over the lazy dog.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <input ref={fileRef} type="file" accept=".ttf,.otf" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'regular')} data-testid="docstyle-font-file" />
              <input ref={boldRef} type="file" accept=".ttf,.otf" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'bold')} data-testid="docstyle-font-bold-file" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!uploading} data-testid="docstyle-upload-font-btn">
                {uploading === 'regular' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload .ttf (regular)
              </Button>
              <Button size="sm" variant="outline" onClick={() => boldRef.current?.click()} disabled={!!uploading || !form.custom_font_s3_key} data-testid="docstyle-upload-font-bold-btn">
                {uploading === 'bold' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload bold
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Uploading a regular .ttf selects it automatically; add a bold weight for headings and totals. Max 3MB.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Layout template</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {LAYOUTS.map((l) => (
                <button key={l.key} type="button" onClick={() => set('layout', l.key)}
                  className={`rounded-lg border p-2 text-left transition-colors ${form.layout === l.key ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-500'}`}
                  data-testid={`docstyle-layout-${l.key}`}>
                  <div className="h-10 rounded-sm mb-1.5 overflow-hidden border border-slate-600 bg-white">
                    {l.key === 'modern' && <div className="h-3" style={{ background: company?.document_branding?.primary_color || '#1E88E5' }} />}
                    {l.key === 'classic' && <div className="mt-3 mx-1 border-t" style={{ borderColor: '#CBD5E1' }} />}
                    {l.key === 'minimal' && <div className="mt-3 mx-1 border-t border-slate-400" style={{ borderWidth: 0.5 }} />}
                  </div>
                  <div className="text-xs font-semibold text-slate-100">{l.label}</div>
                  <div className="text-[10px] text-slate-400 leading-tight">{l.hint}</div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Logo size</Label>
                <Select value={form.logo_size} onValueChange={(v) => set('logo_size', v)}>
                  <SelectTrigger data-testid="docstyle-logo-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="s">Small</SelectItem><SelectItem value="m">Medium</SelectItem><SelectItem value="l">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Logo position</Label>
                <Select value={form.logo_position} onValueChange={(v) => set('logo_position', v)}>
                  <SelectTrigger data-testid="docstyle-logo-position"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Header tagline (optional)</Label>
              <Input value={form.tagline || ''} maxLength={120} onChange={(e) => set('tagline', e.target.value)} placeholder="e.g. Facilities done right, since 1998" data-testid="docstyle-tagline" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Payment instructions</Label>
            <Textarea rows={3} value={form.payment_instructions || ''} maxLength={1200} onChange={(e) => set('payment_instructions', e.target.value)}
              placeholder={'E-transfer to ar@yourcompany.com\nCheques payable to …\nWire: transit / account …'} data-testid="docstyle-payment-instructions" />
            <p className="text-[11px] text-slate-500 mt-1">Printed under "How to pay" on invoices and daily site invoices. Payment terms (Net 30 …) and the footer line live in Settings → Invoices.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Thank-you note</Label>
              <Input value={form.thank_you_note || ''} maxLength={300} onChange={(e) => set('thank_you_note', e.target.value)} placeholder="Thank you for your business!" data-testid="docstyle-thank-you" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-700 px-3 py-2">
              <div><div className="text-sm text-slate-100">Show tax / VAT registration ID</div><div className="text-[11px] text-slate-500">From Settings → Invoices → Tax ID</div></div>
              <Switch checked={form.show_tax_id !== false} onCheckedChange={(v) => set('show_tax_id', v)} data-testid="docstyle-show-tax-id" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-700 px-3 py-2">
              <div><div className="text-sm text-slate-100">Show PO number</div><div className="text-[11px] text-slate-500">Customer purchase-order reference in the header</div></div>
              <Switch checked={form.show_po !== false} onCheckedChange={(v) => set('show_po', v)} data-testid="docstyle-show-po" />
            </div>
          </div>
        </div>

        {colSchema && (
          <div className="pt-4 border-t border-slate-700 space-y-3" data-testid="docstyle-columns-section">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Line-item columns (company default)</Label>
              <p className="text-[12px] text-slate-400 mt-0.5">Rename headers (e.g. NAME → BILL CODE), hide, reorder, choose what data each column shows, or add custom text columns your customers' upload platforms need. A customer's rate card can override this for that customer only.</p>
            </div>
            <div className="flex gap-1 flex-wrap">
              {colSchema.doc_types.map((d) => (
                <button key={d.key} type="button" onClick={() => setColDoc(d.key)}
                  className={`px-3 py-1.5 rounded-md text-xs border ${colDoc === d.key ? 'border-blue-500 bg-blue-500/10 text-blue-200' : 'border-slate-700 text-slate-300 hover:border-slate-500'}`}
                  data-testid={`docstyle-columns-tab-${d.key}`}>
                  {d.label}{columns[d.key] ? ' •' : ''}
                </button>
              ))}
            </div>
            <ColumnLayoutEditor
              docType={colDoc}
              value={columns[colDoc]}
              defaults={colSchema.defaults[colDoc]}
              sources={colSchema.sources[colDoc]}
              onChange={(next) => setColumns((c) => ({ ...c, [colDoc]: next }))}
              testIdPrefix="docstyle-cols"
            />
            <div className="pt-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Header &amp; totals labels — {colSchema.doc_types.find((d) => d.key === colDoc)?.label}</Label>
              <LabelsEditor docType={colDoc} fields={colSchema.label_fields[colDoc]} defaults={colSchema.label_defaults[colDoc]}
                value={labels[colDoc]} onChange={(next) => setLabels((l) => ({ ...l, [colDoc]: next }))} testIdPrefix="docstyle-labels" />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Change note (optional — why)</Label>
                <Input value={colNote} maxLength={500} onChange={(e) => setColNote(e.target.value)} placeholder="e.g. Customer portal now requires bill codes" className="h-8 text-sm" data-testid="docstyle-columns-note" />
              </div>
              <Button size="sm" onClick={saveColumns} disabled={savingCols} data-testid="docstyle-columns-save">
                {savingCols ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save column layout
              </Button>
            </div>

            <div className="pt-3 border-t border-slate-700/60 space-y-2" data-testid="docstyle-presets">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Layout presets</Label>
              <p className="text-[12px] text-slate-400">Save the columns and labels above under a name (e.g. “Suncor upload”, “QuickBooks”). Presets are applied to customers from the customer dialog — applying copies the preset, so later preset edits never change a customer silently.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <Input value={presetName} maxLength={60} onChange={(e) => setPresetName(e.target.value)} placeholder="Template name" className="h-8 text-sm w-60" data-testid="docstyle-preset-name" />
                <Button size="sm" variant="outline" onClick={savePreset} disabled={!presetName.trim()} data-testid="docstyle-preset-save">{editingPresetId ? 'Save template changes' : 'Save as template'}</Button>
                {editingPresetId && <Button size="sm" variant="ghost" onClick={() => { setEditingPresetId(null); setPresetName(''); setTplDefaults({}); setTplNumbering({}); setTplUseNumbering(false); }} data-testid="docstyle-preset-cancel-edit">Cancel</Button>}
              </div>
              <div className="rounded-lg border border-slate-700/60 p-3 space-y-3" data-testid="docstyle-template-extras">
                <p className="text-[11px] text-slate-400">Invoice template defaults — applied when this template is picked in the Daily Invoices builder.</p>
                <TemplateDefaultsFields value={tplDefaults} onChange={setTplDefaults} testPrefix="docstyle-tpl" />
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={tplUseNumbering} onChange={(e) => setTplUseNumbering(e.target.checked)} data-testid="docstyle-tpl-use-numbering" />
                  This template uses its own invoice number sequence (e.g. to match a customer's or accounting system's numbering)
                </label>
                {tplUseNumbering && <NumberingFields value={tplNumbering} onChange={setTplNumbering} testPrefix="docstyle-tpl-numbering" />}
              </div>
              {presets.length > 0 && (
                <ul className="flex flex-wrap gap-2" data-testid="docstyle-preset-list">
                  {presets.map((p) => (
                    <li key={p.id} className="flex items-center gap-1 rounded-full border border-slate-700 pl-3 pr-1 py-0.5 text-xs text-slate-200" data-testid={`docstyle-preset-${p.id}`}>
                      <span>{p.name}</span>
                      {p.numbering && <span className="text-[10px] font-mono text-emerald-300" title="Own number sequence" data-testid={`docstyle-preset-seq-${p.id}`}>#{(p.numbering.counter || 0) + 1}</span>}
                      <button type="button" className="px-1.5 py-0.5 rounded text-blue-300 hover:bg-blue-500/20" onClick={() => loadPresetHere(p)} data-testid={`docstyle-preset-load-${p.id}`}>Load</button>
                      <button type="button" className="px-1.5 py-0.5 rounded text-amber-300 hover:bg-amber-500/20" onClick={() => { loadPresetHere(p); setEditingPresetId(p.id); setPresetName(p.name); setTplDefaults(p.defaults || {}); setTplUseNumbering(!!p.numbering); setTplNumbering(p.numbering ? { prefix: p.numbering.prefix, format: p.numbering.format || '', padding: p.numbering.padding || 4, next_number: (p.numbering.counter || 0) + 1 } : {}); }} data-testid={`docstyle-preset-edit-${p.id}`}>Edit</button>
                      <button type="button" className="px-1.5 py-0.5 rounded text-red-400 hover:bg-red-500/20" onClick={() => deletePreset(p.id)} data-testid={`docstyle-preset-delete-${p.id}`}>Delete</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pt-3 border-t border-slate-700/60 space-y-1.5" data-testid="docstyle-audit">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Recent layout changes (who · what · when · why)</Label>
              <DocumentAuditList items={audit} testIdPrefix="docstyle-audit" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-700">
          <Button onClick={save} disabled={saving} data-testid="docstyle-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save document style
          </Button>
          <Button variant="outline" onClick={openPreview} data-testid="docstyle-preview-pdf">
            <FileText className="w-4 h-4 mr-2" /> Open sample invoice PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
